import React, { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import Select, {
  type SelectInstance,
  type StylesConfig,
} from "react-select";

const MAX_VISIBLE_OPTIONS = 5;

interface SearchableSelectProps {
  id: string;
  label: string;
  labelClassName?: string;
  height?: string;
  required?: boolean;
  value: string;
  options: { value: string; label: string }[];
  onChange: (selectedOption: { value: string; label: string } | null) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  errorText?: string;
  className?: string;
  customStyles?: StylesConfig<{ value: string; label: string }, false>;
}

type SelectOption = { value: string; label: string };

const SearchableSelect = forwardRef<HTMLDivElement, SearchableSelectProps>(
  (
    {
      id,
      label,
      labelClassName = "",
      required = false,
      height,
      value,
      options,
      onChange,
      onKeyDown,
      errorText,
      className,
      customStyles = {},
    },
    ref,
  ) => {
    const selectRef = useRef<SelectInstance<SelectOption, false>>(null);
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");

    const selectedOption = useMemo(
      () => options.find((option) => option.value === value) ?? null,
      [options, value],
    );

    const filteredOptions = useMemo(() => {
      const query = inputValue.trim().toLowerCase();
      const matches = query
        ? options.filter((option) => option.label.toLowerCase().includes(query))
        : options;
      return matches.slice(0, MAX_VISIBLE_OPTIONS);
    }, [inputValue, options]);

    const handleSelectKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (menuIsOpen) {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.stopPropagation();
            return;
          }

          if (event.key === "Enter") {
            event.stopPropagation();
            const select = selectRef.current as
              | (SelectInstance<SelectOption, false> & {
                  state?: { focusedOption?: SelectOption | null };
                })
              | null;
            const focusedOption = select?.state?.focusedOption;

            if (!focusedOption && filteredOptions.length > 0) {
              event.preventDefault();
              onChange(filteredOptions[0]);
              setInputValue("");
              setMenuIsOpen(false);
              select?.blur();
            }
            return;
          }
        }

        onKeyDown?.(event);
      },
      [filteredOptions, menuIsOpen, onChange, onKeyDown],
    );

    const defaultStyles: StylesConfig<SelectOption, false> = {
      control: (base) => ({
        ...base,
        border: "1px solid rgba(2, 2, 2, 0.12)",
        borderRadius: "4px",
        boxShadow: "none",
        height,
      }),
    };

    return (
      <div className="form-group" ref={ref} style={{ lineHeight: height }}>
        <label htmlFor={id} className={labelClassName}>
          {label} {!required && <span className="text-danger">*</span>}
        </label>
        <Select
          ref={selectRef}
          inputId={id}
          value={selectedOption}
          onChange={onChange}
          onKeyDown={handleSelectKeyDown}
          onInputChange={(nextValue, actionMeta) => {
            if (actionMeta.action === "input-change") {
              setInputValue(nextValue);
            }
            if (actionMeta.action === "menu-close") {
              setInputValue("");
            }
          }}
          onMenuOpen={() => setMenuIsOpen(true)}
          onMenuClose={() => {
            setMenuIsOpen(false);
            setInputValue("");
          }}
          options={filteredOptions}
          isSearchable
          openMenuOnFocus
          tabIndex={0}
          className={className}
          classNamePrefix="select"
          filterOption={() => true}
          styles={{ ...defaultStyles, ...customStyles }}
        />
        {errorText && <small className="text-danger mb-5">{errorText}</small>}
      </div>
    );
  },
);

SearchableSelect.displayName = "SearchableSelect";

export default SearchableSelect;
